import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {RoboyuSkin} from 'types'
import {ROBOYU_COLOR_THEME, WINDOW_MODE_MOBILE_WIDTH} from 'consts'
import {useWindowLayout} from 'windows'
import {useTranslation} from 'i18n'
import {useTheme} from 'themes'
import {getThemeColor, showSnackbar, useDidMount} from 'utils'
import {requestData} from 'services'
import {
  Button,
  ButtonOutline,
  Icon,
  List,
  Paragraph,
  RoboyuDefault,
} from 'common/components'
import convertUnit from 'lib/unit'
import {useDispatch, useSelector} from 'lib/redux'

interface ItemContainerProps {
  selected?: boolean
}
const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
`
const StyledSelectedRoboyuContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`
const StyledContentContainer = styled.div`
  display: flex;
  gap: ${convertUnit(40)};
  margin: ${convertUnit(40)};
  flex-direction: row;
  width: 100%;
  height: fit-content;
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    flex-direction: column;
    height: 100%;
    margin: 0;
    gap: 0;
  }
`
const StyledViewerContainer = styled.div`
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  height: fit-content;
  background-color: ${({theme}) => theme.white_2};
  border: ${convertUnit(1)} solid ${({theme}) => theme.gray_1};
  border-radius: ${convertUnit(28)};
  @media (max-width: ${WINDOW_MODE_MOBILE_WIDTH}px) {
    border-radius: 0;
    border-bottom: ${convertUnit(1)} solid ${({theme}) => theme.gray_1};
  }
`
const StyledItemContainer = styled.div<ItemContainerProps>`
  ${({selected, theme}) => ({
    border: selected ? `${convertUnit(2)} solid ${theme.primary_5}` : 'unset',
    borderRadius: convertUnit(16),
  })}
  background-color: ${({theme}) => theme.white_4};
  align-items: center;
  justify-content: center;
  display: flex;
  position: relative;
  width: fit-content;
  overflow: hidden;
  margin-right: ${convertUnit(20)};
  margin-bottom: ${convertUnit(20)};
`

const StyledOverlay = styled.div`
  background-color: ${({theme}) => getThemeColor(theme, 'black', 0.4)};
  position: absolute;
  width: fill-available;
  height: fill-available;
`
const StyledOverlayItem = styled.div`
  display: flex;
  flex-direction: column;
  position: absolute;
`

const StyledButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${convertUnit(8)};
  position: absolute;
  bottom: 0;
  justify-content: space-between;
  width: calc(100% - (${convertUnit(40)}));
  margin-bottom: ${convertUnit(20)};
`
const StyledButton = styled(Button)`
  width: fit-content;
`
const StyledButtonOutline = styled(ButtonOutline)`
  width: fit-content;
`

export default function GiftShopRobopetCustomize() {
  const theme = useTheme()
  const {colorTheme: currColorTheme} = useSelector('roboyuState')
  const [selected, setSelected] = useState<RoboyuSkin>(
    currColorTheme.name === 'default'
      ? {skin_id: '', name: 'blue'}
      : currColorTheme,
  )
  const {translate} = useTranslation()
  const {update} = useDispatch()
  const {height, width} = useWindowLayout()
  const [skinList, setSkinList] = useState<ReadonlyArray<RoboyuSkin>>(
    ROBOYU_COLOR_THEME,
  )

  const isButtonDisabled = useMemo(
    () =>
      selected.name ===
      (currColorTheme.name === 'default' ? 'blue' : currColorTheme.name),
    [currColorTheme, selected],
  )

  const handleReset = useCallback(() => {
    setSelected(currColorTheme)
  }, [currColorTheme])

  const handleSave = useCallback(() => {
    if (currColorTheme.name !== selected.name) {
      requestData('roboyu_patch_skin', {
        data: {skin_id: selected.skin_id},
        onRequestSuccess: ({status}) => {
          if (status === 200) {
            showSnackbar(translate('global:success'))
            update('roboyuState', {colorTheme: selected})
          }
        },
      })
    }
  }, [currColorTheme.name, selected, translate, update])

  const handleRenderItem = useCallback(
    (roboyuTheme: RoboyuSkin) => (
      <StyledItemContainer
        selected={
          roboyuTheme.name ===
          (currColorTheme.name === 'default' ? 'blue' : selected.name)
        }
        className={
          skinList[skinList.indexOf(roboyuTheme)].skin_id
            ? 'pressable'
            : undefined
        }
        onClick={() => {
          if (
            selected.name !== roboyuTheme.name &&
            skinList[skinList.indexOf(roboyuTheme)].skin_id
          ) {
            setSelected(roboyuTheme)
          }
        }}>
        <RoboyuDefault
          colorTheme={roboyuTheme.name}
          width="100%"
          height="100%"
        />
        {skinList[skinList.indexOf(roboyuTheme)].skin_id ? null : (
          <>
            <StyledOverlay />
            <StyledOverlayItem>
              <Icon type="padlock" color="white_1" size={16} />
              <Paragraph color="white_1" fontSize="xs" fontWeight="bold">
                {`LEVEL ${ROBOYU_COLOR_THEME.indexOf(roboyuTheme) * 5}`}
              </Paragraph>
            </StyledOverlayItem>
          </>
        )}
      </StyledItemContainer>
    ),
    [currColorTheme, selected, skinList],
  )

  const handleFetchSkinList = useCallback(() => {
    requestData('roboyu_skin_list', {
      onRequestSuccess: ({status, data}) => {
        if (status === 200) {
          setSkinList((prev) =>
            prev.map((v, idx) => {
              if (idx > data.result.length - 1) return v
              return v.name === data.result[idx].name
                ? {...v, skin_id: data.result[idx].skin_id}
                : v
            }),
          )
        }
      },
    })
  }, [])

  useDidMount(handleFetchSkinList)

  return (
    <StyledContainer>
      <StyledContentContainer>
        <StyledViewerContainer>
          <StyledSelectedRoboyuContainer>
            <RoboyuDefault
              width={
                width < WINDOW_MODE_MOBILE_WIDTH
                  ? '100%'
                  : convertUnit(width * 0.35)
              }
              height={
                width < WINDOW_MODE_MOBILE_WIDTH
                  ? convertUnit(height * 0.35)
                  : '100%'
              }
              colorTheme={selected.name}
            />
          </StyledSelectedRoboyuContainer>
          <StyledButtonContainer>
            <StyledButtonOutline
              label={translate('global:reset')}
              color="gray_5"
              onClick={handleReset}
              type="smallest"
              fontSize="s"
              disabled={isButtonDisabled}
            />
            <StyledButton
              label={translate('global:save')}
              onClick={handleSave}
              type="smallest"
              fontSize="s"
              disabled={isButtonDisabled}
            />
          </StyledButtonContainer>
        </StyledViewerContainer>
        <List
          renderItem={handleRenderItem}
          keyExtractor={(item) => item.name}
          scrollbar={false}
          data={skinList}
          numColumns={4}
          contentContainerStyle={{
            paddingLeft: convertUnit(20),
            paddingTop: convertUnit(20),
            height: '100%',
            boxSizing: 'border-box',
            border:
              width < WINDOW_MODE_MOBILE_WIDTH
                ? 'unset'
                : `${convertUnit(1)} solid ${theme.gray_1}`,
            borderRadius: convertUnit(
              width < WINDOW_MODE_MOBILE_WIDTH ? 0 : 28,
            ),
          }}
        />
      </StyledContentContainer>
    </StyledContainer>
  )
}
